import React from 'react';
import {Route} from 'react-router';
import {
  AlertDisplay,
  OAuthRequestDialog,
  SignInPage,
} from '@backstage/core-components';
import { FlatRoutes, AppRouter } from '@backstage/core-app-api';
import { createApp } from '@backstage/app-defaults';
import { apiDocsPlugin, ApiExplorerPage } from '@backstage/plugin-api-docs';
import {
  CatalogEntityPage,
  CatalogIndexPage,
  catalogPlugin,
} from '@backstage/plugin-catalog';
import {
  CatalogImportPage,
  catalogImportPlugin,
} from '@backstage/plugin-catalog-import';
import { ScaffolderPage, scaffolderPlugin } from '@backstage/plugin-scaffolder';
import { entityPage } from './components/catalog/EntityPage';
import { SearchPage } from '@backstage/plugin-search';
import { searchPage } from './components/search/SearchPage';
import {
  techdocsPlugin,
  TechDocsReaderPage,
  TechDocsCustomHome
} from '@backstage/plugin-techdocs';
import { UserSettingsPage } from '@backstage/plugin-user-settings';
import { apis } from './apis';
import { Root } from './components/Root';
import * as plugins from './plugins';
import { providers } from './identityProviders';
import { UsefulLinksPage } from '@internal/plugin-useful-links';
import { CompliancePage } from '@internal/plugin-compliance';
import { ChangeManagementPage } from '@internal/plugin-change-management';
import { orgPlugin } from '@backstage/plugin-org';
import { PatchManagementPage } from '@internal/plugin-patch-management';
import { HomepageCompositionRoot } from '@backstage/plugin-home';
import { homePage } from './components/home/HomePage';
import { leoDarkTheme, leoLightTheme } from "./components/theme/custom-themes";
import LogoIcon from "./components/Root/LogoIcon";
import { JiraSyncPage } from '@internal/plugin-jira-sync';
import {techdocsTabs} from "./components/techdocs/techdocsTabs";
import {CssBaseline, ThemeProvider} from "@material-ui/core";
import { CatalogUnprocessedEntitiesPage } from "@backstage/plugin-catalog-unprocessed-entities";

/**
 * This instantiates the frontend. If you need to change things here
 * it is usually well documented.
 */
const app = createApp({
  apis,
  plugins: Object.values(plugins),
  themes: [
    {
      id: "leo-dark",
      title: "Leo Dark",
      variant: "dark",
      icon: <LogoIcon />,
      Provider: ({children}) => (
        <ThemeProvider theme={leoDarkTheme}>
          <CssBaseline>{children}</CssBaseline>
        </ThemeProvider>
      )
    },{
      id: "leo-light",
      title: "Leo Light",
      variant: "light",
      icon: <LogoIcon />,
      Provider: ({children}) => (
        <ThemeProvider theme={leoLightTheme}>
          <CssBaseline>{children}</CssBaseline>
        </ThemeProvider>
      )
    },
  ],
  components: {
    SignInPage: (props) => {
      return (
        <SignInPage
          onSignInSuccess={props.onSignInSuccess}
          children={props.children}
          providers={['guest',...providers]}
          title="Select a sign-in method"
          align="center"
        />
      );
    },
  },
  bindRoutes({ bind }) {
    bind(catalogPlugin.externalRoutes, {
      createComponent: scaffolderPlugin.routes.root as any,
      viewTechDoc: techdocsPlugin.routes.docRoot as any,
      createFromTemplate: scaffolderPlugin.routes.selectedTemplate as any,
    });
    bind(apiDocsPlugin.externalRoutes, {
      registerApi: catalogImportPlugin.routes.importPage as any,
    });
    bind(scaffolderPlugin.externalRoutes, {
      registerComponent: catalogImportPlugin.routes.importPage as any,
      viewTechDoc: techdocsPlugin.routes.docRoot as any,
    });
    bind(orgPlugin.externalRoutes, {
      catalogIndex: catalogPlugin.routes.catalogIndex as any,
    });
  },
});

/**
  This is a definition of all the routes to pages in backstage.<br>
  You can add a Route here for any frontend. Once you have a defined a Route you may way want to update
  packages/app/src/components/Root/Root.tsx

 */
const routes = (
  <FlatRoutes>
    <Route path="/" element={<HomepageCompositionRoot />}>
      {homePage}
    </Route>
    <Route path="/catalog" element={<CatalogIndexPage />} />
    <Route
      path="/catalog/:namespace/:kind/:name"
      element={<CatalogEntityPage />}
    >
      {entityPage}
    </Route>
    <Route path="/docs" element={<TechDocsCustomHome tabsConfig={techdocsTabs()} />} />
    <Route
      path="/docs/:namespace/:kind/:name/*"
      element={<TechDocsReaderPage />}
    />
    <Route
      path="/catalog-unprocessed-entities"
      element={<CatalogUnprocessedEntitiesPage />}
    />
    <Route path="/create" element={<ScaffolderPage />} />
    <Route path="/api-docs" element={<ApiExplorerPage />} />
    <Route path="/catalog-import" element={<CatalogImportPage />} />
    <Route path="/search" element={<SearchPage />}>
      {searchPage}
    </Route>
    <Route path="/settings" element={<UserSettingsPage />} />
    <Route path="/useful-links" element={<UsefulLinksPage />} />
    <Route path="/compliance" element={<CompliancePage />} />
    <Route path="/change-management" element={<ChangeManagementPage />} />
    <Route path="/patch-management" element={<PatchManagementPage />}/>
    <Route path="/jira-sync" element={<JiraSyncPage />}/>
  </FlatRoutes>
);

// Routes are added here
export default app.createRoot(
  <>
    <AlertDisplay />
    <OAuthRequestDialog />
    <AppRouter>
      <Root>{routes}</Root>
    </AppRouter>
  </>,
);
